import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Gift, Lock, Clock, ArrowLeft, Sparkles } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function BonusPreviewPage() {
  const { presaveId } = useParams();
  const { user, isAuthenticated } = useAuth();
  const [presave, setPresave] = useState(null);
  const [hasPresaved, setHasPresaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get presave details
        const presaveRes = await axios.get(`${API_URL}/presaves/${presaveId}`);
        setPresave(presaveRes.data);

        // Check if user has pre-saved (if logged in)
        if (isAuthenticated && user) {
          try {
            const checkRes = await axios.get(`${API_URL}/presaves/${presaveId}/check-presave`);
            setHasPresaved(checkRes.data.has_presaved);
          } catch (e) {
            // Endpoint might not exist yet, default to false
            setHasPresaved(false);
          }
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load bonus preview');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [presaveId, isAuthenticated, user]);

  if (loading) {
    return (
      <div className="main-content min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error || !presave) {
    return (
      <div className="main-content min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Bonus Not Found</h1>
          <p className="text-gray-400 mb-6">{error || 'This bonus preview does not exist.'}</p>
          <Link to="/pre-save" className="btn-primary">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Pre-saves
          </Link>
        </div>
      </div>
    );
  }

  const isReleased = presave.is_released;
  const hasBonus = presave.bonus_content_url;
  const releaseDate = new Date(presave.release_date);
  const now = new Date();
  const daysUntil = Math.ceil((releaseDate - now) / (1000 * 60 * 60 * 24));

  return (
    <div className="main-content min-h-screen py-24">
      <div className="container max-w-2xl mx-auto px-4">
        {/* Back Link */}
        <Link to="/pre-save" className="inline-flex items-center text-gray-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Pre-saves
        </Link>

        {/* Main Card */}
        <div className="glass-card p-8 relative overflow-hidden">
          {/* Background Effects */}
          <div className="absolute -top-20 -right-20 w-60 h-60 bg-purple-500/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-cyan-500/20 rounded-full blur-3xl" />

          <div className="relative z-10">
            {/* Header */}
            <div className="flex items-center justify-center gap-2 text-purple-400 mb-4">
              <Gift className="w-5 h-5" />
              <span className="text-sm font-medium uppercase tracking-wider">Pre-save Bonus</span>
            </div>

            {/* Cover Art */}
            <div className="relative mx-auto w-64 h-64 mb-6">
              <img 
                src={presave.cover_url} 
                alt={presave.title}
                className={`w-full h-full rounded-xl object-cover shadow-2xl ${!hasPresaved && !isReleased ? 'blur-sm' : ''}`}
              />
              {!hasPresaved && !isReleased && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-xl">
                  <Lock className="w-12 h-12 text-white/70" />
                </div>
              )}
            </div>

            {/* Title */}
            <h1 className="text-3xl font-bold text-center mb-2">{presave.title}</h1>
            <p className="text-gray-400 text-center mb-6">{presave.description}</p>

            {/* Bonus Content Section */}
            {hasBonus ? (
              <div className="bg-gradient-to-r from-purple-500/20 to-cyan-500/20 rounded-xl p-6 mb-6 border border-purple-500/30">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-5 h-5 text-yellow-400" />
                  <h3 className="font-semibold text-lg">Exclusive Bonus Content</h3>
                </div>
                
                <p className="text-gray-300 mb-4">
                  {presave.bonus_description || `Exclusive ${presave.bonus_content_type || 'content'} for fans who pre-saved!`}
                </p>

                {/* Access Status */}
                {isReleased && hasPresaved ? (
                  // Released and user has pre-saved - show full access
                  <a 
                    href={presave.bonus_content_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary w-full text-center"
                  >
                    <Gift className="w-4 h-4 mr-2 inline" />
                    Access Your Bonus
                  </a>
                ) : isReleased && !hasPresaved ? (
                  // Released but user didn't pre-save
                  <div className="text-center py-4">
                    <Lock className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                    <p className="text-gray-400">This bonus was exclusive to fans who pre-saved.</p>
                  </div>
                ) : hasPresaved ? (
                  // Not released yet but user has pre-saved
                  <div className="text-center py-4">
                    <Clock className="w-8 h-8 text-cyan-400 mx-auto mb-2 animate-pulse" />
                    <p className="text-cyan-400 font-medium">You're all set!</p>
                    <p className="text-gray-400 text-sm mt-1">
                      Your bonus unlocks in {daysUntil > 0 ? `${daysUntil} day${daysUntil > 1 ? 's' : ''}` : 'less than a day'}
                    </p>
                  </div>
                ) : (
                  // Not released and user hasn't pre-saved
                  <div className="text-center py-4">
                    <Lock className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                    <p className="text-gray-400 mb-4">Pre-save now to unlock this exclusive bonus on release day!</p>
                    <Link to={`/pre-save`} className="btn-primary">
                      Pre-save Now
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <p>No bonus content available for this release.</p>
              </div>
            )}

            {/* Release Date */}
            <div className="text-center text-sm text-gray-500">
              {isReleased ? (
                <span className="text-green-400">✓ Released on {releaseDate.toLocaleDateString()}</span>
              ) : (
                <span>Releases: {releaseDate.toLocaleDateString()} at {releaseDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
